import { parse } from "date-fns";
import { request } from "@/Api";

function clampNumber(number, min, max) {
    return Math.min(Math.max(number, min), max);
}

function queryToFilterValue(query) {
    const value = query ? query.split(":")[1] : null;
    return value == null ? null : isNaN(Number(value)) ? value : Number(value);
}

function dateQueryToFilterValue(query) {
    if (!query) {
        return null;
    }
    const date = parse(
        query.split(":")[1] + " 00:00:00",
        "y-MM-dd HH:mm:ss",
        new Date(),
    );
    return date == "Invalid Date" ? null : date;
}

function formattedNumber(number) {
    const options = {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    };
    return parseFloat(number).toLocaleString("en-US", options);
}

function formattedCurrency(number, currency, trimSymbol = false, digits = 0) {
    number = parseFloat(number);
    if (isNaN(number)) {
        return "";
    }

    if (!currency) {
        return number.toLocaleString();
    }

    if (trimSymbol) {
        let symbol = getCurrencySymbol(currency);
        symbol = symbol.charAt(symbol.length - 1);
        return (
            symbol +
            number.toLocaleString(undefined, {
                minimumFractionDigits: digits,
                maximumFractionDigits: digits,
            })
        );
    }

    return (
        (currency == "CAD" ? "CA" : "") +
        new Intl.NumberFormat("en-CA", {
            style: "currency",
            currency: currency,
            maximumFractionDigits: digits,
        }).format(number)
    );
}

function getCurrencySymbol(currency) {
    if (!currency) {
        return "";
    }
    try {
        return (
            (currency == "CAD" ? "CA" : "") +
            (0)
                .toLocaleString("en-CA", {
                    style: "currency",
                    currency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                })
                .replace(/\d/g, "")
                .trim()
        );
    } catch {
        return "";
    }
}

function statusQueryToFilterValue(query) {
    return query ? query.split(":")[1].split(",") : [];
}

function getDataFromQueryParams(defaults) {
    const queryParams = window.location.search;
    let parsedParams = new URLSearchParams(queryParams);

    let allDefault = false;
    const page =
        parsedParams.has("offset") && parsedParams.has("limit")
            ? parseInt(parsedParams.get("offset") / parsedParams.get("limit"))
            : defaults.page;
    const limit = parsedParams.has("limit")
        ? parseInt(parsedParams.get("limit"))
        : defaults.limit;
    const orders = parsedParams.has("orders")
        ? parsedParams.get("orders")
        : defaults.orders;

    parsedParams.delete("orders");
    parsedParams.delete("offset");
    parsedParams.delete("limit");

    let filters = {};
    for (const [key, value] of parsedParams) {
        if (parsedParams.getAll(key).length > 1) {
            filters[key] = parsedParams.getAll(key);
        } else {
            filters[key] = value;
        }
    }

    if (
        page == defaults.page &&
        limit == defaults.limit &&
        orders == defaults.orders &&
        Object.keys(filters).length == 0
    ) {
        allDefault = true;
    }

    return {
        page: page,
        limit: limit,
        orders: orders,
        filters: filters,
        allDefault: allDefault,
    };
}

const downloadPdfFromHtmlString = (htmlString, name) => {
    request
        .post(
            "/pdf",
            { html_string: htmlString, name: name },
            { responseType: "blob" },
        )
        .then((response) => {
            downloadPdfFromResponse(response);
        });
};

const downloadFile = (url, params = {}, callback) => {
    request
        .get(url, {
            responseType: "blob",
            params: params,
        })
        .then((response) => {
            downloadPdfFromResponse(response);
            if (callback) {
                callback();
            }
        })
        .catch((error) => {
            console.error("Error downloading file:", error);
        });
};

const downloadPdfFromResponse = (response) => {
    const href = URL.createObjectURL(response.data);
    const filename = response.headers["content-disposition"].split("=")[1];

    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
};

function generateRandomColor() {
    var minBrightness = 60;
    var maxBrightness = 220;
    var red =
        Math.floor(Math.random() * (maxBrightness - minBrightness + 1)) +
        minBrightness;
    var green =
        Math.floor(Math.random() * (maxBrightness - minBrightness + 1)) +
        minBrightness;
    var blue =
        Math.floor(Math.random() * (maxBrightness - minBrightness + 1)) +
        minBrightness;
    var colorHex =
        "#" +
        ((1 << 24) | (red << 16) | (green << 8) | blue).toString(16).slice(1);
    return colorHex;
}

function findCommonElements(arrays) {
    const referenceArray = arrays[0];
    if (!referenceArray) {
        return;
    }
    const commonElements = referenceArray.filter((element) =>
        arrays.every((array) => array.includes(element)),
    );
    return commonElements;
}

function getNth(n) {
    return ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";
}

function abbreviateNumber(number) {
    return Intl.NumberFormat("en-US", {
        notation: "compact",
    }).format(number);
}

function parseVideoUrlToEmbedUrl(url) {
    if (url.includes("youtube")) {
        if (url.includes("embed")) {
            return url;
        } else {
            return url.replace("watch?v=", "embed/").split("&")[0];
        }
    } else if (url.includes("youtu.be")) {
        return url.replace("youtu.be", "youtube.com/embed");
    } else if (url.includes("vimeo")) {
        if (url.includes("player.vimeo.com/video/")) {
            return url + "?width=2000";
        } else {
            return url
                .replace("vimeo.com", "player.vimeo.com/video")
                .replace("?share=copy", "");
        }
    } else if (url.includes("drive.google.com")) {
        if (url.includes("preview")) {
            return url;
        } else {
            return url.replace("view", "preview");
        }
    }
    return url;
}

function parseNameToInitials(name) {
    const words = name.split(" ");
    const initialsNum = 2;
    let initials = "";
    for (var i = 0; i < initialsNum; i++) {
        if (!words[i]) {
            return initials;
        }
        initials += words[i].substring(0, 1).toUpperCase();
    }
    return initials;
}

function uuidv4() {
    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) =>
        (
            +c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
        ).toString(16),
    );
}

function getComplementarySet(universalSet, subset) {
    const subsetSet = new Set(subset);
    return universalSet.filter((element) => !subsetSet.has(element));
}

function formattedPercentage(number, decimal) {
    return `${(number * 100).toFixed(decimal)}%`;
}

function parseBool(string) {
    if (string == "true") {
        return true;
    } else if (string == "false") {
        return false;
    } else {
        return false;
        // throw (`when parsing a bool, string of true or false expected, got ${string} instead`)
    }
}

function tryParseJson(str) {
    try {
        return JSON.parse(str);
    } catch (e) {
        return null;
    }
}

function sum(array) {
    return array.reduce((a, b) => a + b, 0);
}

function emailIsValid(email) {
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(email);
}

function truncateString(str, length = 20) {
    return str.length > length ? str.slice(0, length) + "..." : str;
}

function capitalizeFirstLetter(str) {
    if (typeof str !== "string" || str.length === 0) {
        return str;
    }
    return str.charAt(0).toUpperCase() + str.slice(1);
}

function formatDateStringToDayMonth(dateStr) {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    return `${day} ${month}`;
}

function formatDateStringToDayMonthYear(dateStr) {
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
}

function formatDateToDetailed(date) {
    if (!date) return "";
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "";
    const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
    ];
    const month = months[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = d.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? "0" + minutes : minutes;
    return `${month} ${day}, ${year}, ${hours}:${minutesStr}${ampm}`;
}

const sortByCustomOrder = (arr, key, orderArray) => {
    return [...arr].sort((a, b) => {
        const indexA = orderArray.indexOf(a[key]);
        const indexB = orderArray.indexOf(b[key]);

        const weightA = indexA === -1 ? Infinity : indexA;
        const weightB = indexB === -1 ? Infinity : indexB;

        return weightA - weightB;
    });
};

export {
    formattedCurrency,
    getCurrencySymbol,
    queryToFilterValue,
    dateQueryToFilterValue,
    statusQueryToFilterValue,
    getDataFromQueryParams,
    downloadFile,
    downloadPdfFromHtmlString,
    generateRandomColor,
    formattedNumber,
    findCommonElements,
    getNth,
    clampNumber,
    abbreviateNumber,
    parseVideoUrlToEmbedUrl,
    parseNameToInitials,
    uuidv4,
    getComplementarySet,
    formattedPercentage,
    parseBool,
    sum,
    emailIsValid,
    capitalizeFirstLetter,
    tryParseJson,
    truncateString,
    formatDateStringToDayMonth,
    formatDateStringToDayMonthYear,
    formatDateToDetailed,
    sortByCustomOrder,
};
